import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/lib/constants";

const LINKS = [
  { id: "portfolio", label: "Portfolio" },
  { id: "invite-types", label: "Invite Types" },
  { id: "services", label: "Services" },
  { id: "stories", label: "Stories" },
  { id: "instagram", label: "Instagram" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/65 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between py-5">
        {/* Brand mark */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
          aria-label="InvitationWala home"
          data-testid="nav-brand"
        >
          <img
            src={BRAND.logo}
            alt={`${BRAND.name} — Premium Digital Wedding Invitation Studio · Home`}
            className="h-10 md:h-12 w-auto object-contain"
            width="120"
            height="48"
            decoding="async"
            style={{
              filter: "drop-shadow(0 2px 8px rgba(232,165,180,0.3))",
              mixBlendMode: "normal",
            }}
          />
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="link-underline text-[var(--iw-ivory-dim)] text-sm tracking-wide hover:text-[var(--iw-gold)] transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollTo("schedule")}
            className="btn-gold hidden md:inline-flex"
            data-testid="nav-schedule-cta"
          >
            Request a Quote
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-[var(--iw-ivory)] p-2"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden bg-black border-t border-white/10"
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className="text-left text-[var(--iw-ivory)] text-lg font-serif-display"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
                data-testid={`nav-mobile-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("schedule")}
              className="btn-gold mt-2 w-fit"
              data-testid="nav-mobile-schedule-cta"
            >
              Request a Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
